import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  GraduationCap,
  BookOpen,
  Briefcase,
  Building2,
  Euro,
  Calendar,
  Shield,
  Clock,
  Globe2,
  Users,
  Award,
  TrendingUp,
  Mail,
  MessageCircle,
  ChevronRight,
  Star,
  AlertCircle,
  Sparkles,
  Cpu,
  Stethoscope,
  Wrench,
  FileText,
  HelpCircle,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { usePageMeta, useServiceSchema, useFAQSchema } from '../../hooks/useSEO';
import BreadcrumbSchema from '../../components/seo/BreadcrumbSchema';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';

/* ────────────────────────────────────────────────────────────────── */
/* DATA                                                                */
/* ────────────────────────────────────────────────────────────────── */

const keyReasons = [
  { factor: 'Tuition-Free Public Universities', details: '€0 tuition at most public universities — Germany invests in talent regardless of nationality.', icon: Euro },
  { factor: 'World-Class Engineering', details: 'Home of Mercedes, BMW, Siemens, SAP. 5 universities in the global Top 100 for engineering.', icon: Wrench },
  { factor: 'Opportunity Card (2024)', details: 'Points-based 1-year job-seeker visa launched June 2024 — easier than ever to land in Germany.', icon: Sparkles },
  { factor: '18-Month Job Seeker Visa', details: 'After graduation, stay 18 months to find skilled employment matching your degree.', icon: Briefcase },
  { factor: 'Clear PR Pathway', details: 'After just 2 years of full-time skilled work, apply for Germany Permanent Residence (Niederlassungserlaubnis).', icon: Shield },
  { factor: 'EU Blue Card', details: 'Earn €45,300+ in a skilled role and qualify for the EU Blue Card → PR in 21 months.', icon: Award },
];

const universityTypes = [
  {
    type: 'Universität (Research Universities)',
    focus: 'Theoretical / research-driven · Doctorates available',
    examples: 'TU Munich, Heidelberg, LMU Munich, RWTH Aachen',
    best: 'Engineering, Sciences, Medicine, PhD aspirants',
    color: 'from-[#003087]/10 to-[#003087]/5 border-[#003087]/40',
  },
  {
    type: 'Fachhochschule (Universities of Applied Sciences)',
    focus: 'Practical / industry-focused · Mandatory internships',
    examples: 'Hochschule München, FH Aachen, HTW Berlin',
    best: 'Career-focused students wanting fast industry entry',
    color: 'from-[#C5A028]/10 to-[#C5A028]/5 border-[#C5A028]/40',
  },
  {
    type: 'Duale Hochschule (Cooperative Universities)',
    focus: 'Study + Paid Work-Integrated · Contract with employer',
    examples: 'DHBW Stuttgart, DHBW Mannheim',
    best: 'Students seeking guaranteed German salary while studying',
    color: 'from-[#FF1F2D]/10 to-[#FF1F2D]/5 border-[#FF1F2D]/40',
  },
];

const gradingScale = [
  { grade: '1.0 – 1.5', label: 'Sehr Gut (Very Good)', equiv: '90%+ / A+', note: 'Top scholarship-eligible' },
  { grade: '1.6 – 2.5', label: 'Gut (Good)', equiv: '75–89% / A', note: 'Required for most TUs' },
  { grade: '2.6 – 3.5', label: 'Befriedigend (Satisfactory)', equiv: '60–74% / B', note: 'Most public universities' },
  { grade: '3.6 – 4.0', label: 'Ausreichend (Sufficient)', equiv: 'Pass / C', note: 'Minimum passing grade' },
  { grade: '5.0', label: 'Nicht Bestanden (Fail)', equiv: 'Fail', note: 'Must repeat' },
];

const apsSteps = [
  { n: 1, title: 'Registration', desc: 'Register online at the APS portal (aps-india.de) and pay the fee of ₹15,500 (approx €170).' },
  { n: 2, title: 'Document Submission', desc: 'Submit 2 originals + 2 attested copies of all academic transcripts, mark-sheets, degrees, and IELTS/TestDaF.' },
  { n: 3, title: 'Document Verification', desc: 'APS-India verifies authenticity with your university registrar — takes 3–4 weeks.' },
  { n: 4, title: 'Personal Interview (Skype/In-Person)', desc: 'A short academic interview about your degree, subjects, and motivation to study in Germany. Conducted in English.' },
  { n: 5, title: 'APS Certificate Issued', desc: 'Receive 10 hardcopies of the APS Certificate (validity: lifetime). Mandatory for university applications + visa.' },
];

const apsTimeline = [
  { period: 'Now – T-12 months', task: 'Start APS registration & document collection' },
  { period: 'T-10 to T-9 months', task: 'Receive APS certificate · Begin university shortlisting' },
  { period: 'T-8 to T-6 months', task: 'Submit university applications (Uni-Assist or direct)' },
  { period: 'T-5 to T-4 months', task: 'Receive admission letter · Open Blocked Account · Buy health insurance' },
  { period: 'T-3 months', task: 'Apply for student visa at German Consulate · Biometrics' },
  { period: 'T-1 month', task: 'Visa stamped · Book flights · Pre-departure briefing' },
];

const apsInterviewTopics = [
  'Why Germany over UK/Canada/USA?',
  'Explain your final-year project / thesis in detail',
  'Why this specific course and university?',
  'Walk me through your transcripts — any backlogs?',
  'How will this degree help your career goals?',
  'What is your post-study plan in Germany?',
];

const topCourses = [
  {
    category: 'Engineering',
    title: 'Mechanical / Automotive / Aerospace',
    salary: '€48,000 – 68,000',
    inr: '~₹43–61 L',
    desc: 'TU Munich, RWTH Aachen, KIT, Stuttgart. Direct hiring pipeline into Mercedes, BMW, Audi, Bosch.',
    icon: Wrench,
  },
  {
    category: 'IT & Data',
    title: 'CS, AI & Data Science',
    salary: '€55,000 – 75,000',
    inr: '~₹49–67 L',
    desc: 'TU Munich, TU Berlin, KIT. Massive demand at SAP, Siemens, BMW AI, plus 200+ Berlin/Munich startups.',
    icon: Cpu,
  },
  {
    category: 'Healthcare',
    title: 'Nursing, Medicine, Public Health',
    salary: '€42,000 – 62,000',
    inr: '~₹38–55 L',
    desc: 'Heidelberg, Charité Berlin, LMU Munich. EU recognition for Indian MBBS via Approbation route.',
    icon: Stethoscope,
  },
  {
    category: 'Business',
    title: 'MBA, Finance, Project Management',
    salary: '€52,000 – 80,000',
    inr: '~₹46–72 L',
    desc: 'Mannheim Business School, Frankfurt School, ESMT Berlin. EU\'s 5th-largest finance hub.',
    icon: Briefcase,
  },
];

const tu9Universities = [
  { name: 'TU Munich', qs: 'QS #28', focus: 'Engineering, AI, Robotics', slug: 'technical-university-of-munich' },
  { name: 'RWTH Aachen', qs: 'QS #99', focus: 'Mechanical & Electrical Engineering', slug: 'rwth-aachen-university' },
  { name: 'KIT (Karlsruhe)', qs: 'QS #119', focus: 'Computer Science, Physics', slug: 'kit-karlsruhe' },
  { name: 'TU Berlin', qs: 'QS #154', focus: 'Industrial Engineering, IT', slug: 'tu-berlin' },
  { name: 'TU Darmstadt', qs: 'QS #205', focus: 'Information Technology, Cybersecurity', slug: 'tu-darmstadt' },
  { name: 'TU Dresden', qs: 'QS #195', focus: 'Material Science, Microelectronics', slug: 'tu-dresden' },
  { name: 'University of Stuttgart', qs: 'QS #312', focus: 'Aerospace, Automotive', slug: 'university-of-stuttgart' },
  { name: 'TU Hannover', qs: 'QS #295', focus: 'Mechanical Engineering, Production', slug: 'tu-hannover' },
  { name: 'TU Braunschweig', qs: 'QS #380', focus: 'Aerospace, Mobility Engineering', slug: 'tu-braunschweig' },
];

const excellenceUniversities = [
  { name: 'University of Heidelberg', qs: 'QS #84', focus: 'Medicine, Sciences, Humanities', slug: 'university-of-heidelberg' },
  { name: 'LMU Munich', qs: 'QS #54', focus: 'Sciences, Medicine, Business', slug: 'lmu-munich' },
  { name: 'Humboldt Berlin', qs: 'QS #120', focus: 'Humanities, Natural Sciences', slug: 'humboldt-berlin' },
  { name: 'University of Tübingen', qs: 'QS #213', focus: 'Neuroscience, Theology', slug: 'university-of-tubingen' },
];

const opportunityCardPoints = [
  { criterion: 'Age', detail: '< 35 years (4 points) · 35–40 yrs (3 pts) · 41+ (1 pt)', max: 4 },
  { criterion: 'Qualifying Degree / Vocational Training', detail: '4 points (recognised qualification)', max: 4 },
  { criterion: 'German Language', detail: 'A1 (1 pt) · A2 (2) · B1 (3) · B2+ (4)', max: 4 },
  { criterion: 'English Language', detail: 'B2 (1 pt)', max: 1 },
  { criterion: 'Work Experience', detail: '2 years in last 5 (2 pts) · 5 years in last 7 (3 pts)', max: 3 },
  { criterion: 'Age + Bonus Criteria', detail: 'Prior Germany stay 6 months+ (1 pt) · Spouse joining (1 pt)', max: 2 },
];

const totalCostComparison = [
  { country: 'Germany (Public Uni)', tuition: '€0', living: '€11,208/yr', total: '~₹10 L', icon: '🇩🇪' },
  { country: 'United Kingdom', tuition: '£18–35k', living: '£12–15k/yr', total: '~₹35–55 L', icon: '🇬🇧' },
  { country: 'Canada', tuition: 'CAD 18–30k', living: 'CAD 20,635/yr', total: '~₹47–93 L', icon: '🇨🇦' },
  { country: 'Australia', tuition: 'AUD 35–48k', living: 'AUD 24,505/yr', total: '~₹45–65 L', icon: '🇦🇺' },
];

const visaSteps = [
  { n: 1, title: 'University Admission Letter (Zulassung)', desc: 'Receive admission letter (conditional or final) from a recognised German university.' },
  { n: 2, title: 'Open Blocked Account', desc: 'Deposit €11,208 in Fintiba, Expatrio, Deutsche Bank or Kotak Blocked Account.' },
  { n: 3, title: 'Buy Health Insurance', desc: 'Purchase mandatory German health insurance (TK, AOK, or Mawista — ~€110/month).' },
  { n: 4, title: 'Schedule Visa Appointment', desc: 'Book interview slot at the nearest German Consulate (Delhi, Mumbai, Chennai, Kolkata, Bengaluru).' },
  { n: 5, title: 'Submit Documents + Biometrics', desc: 'Attend appointment with APS, Blocked Account proof, insurance, admission letter + give biometrics.' },
  { n: 6, title: 'Visa Decision (4–12 Weeks)', desc: 'Receive National Visa (D-type) — book flight, arrive in Germany, register at Bürgeramt within 14 days.' },
];

const faqs = [
  {
    q: 'Is Germany good for Indian students in 2026?',
    a: 'Yes — Germany hosts 40,000+ Indian students (2026), most studying STEM. With €0 public tuition, 18-month job-seeker visa, the new Opportunity Card, and EU Blue Card → PR in just 21 months, it offers the best total-cost ROI of any major destination.',
  },
  {
    q: 'What is the APS Certificate and why is it mandatory?',
    a: 'The APS (Akademische Prüfstelle) Certificate is a mandatory document for all Indian students applying to German universities since November 2022. It verifies the authenticity of your academic credentials. No APS = no university admission AND no student visa.',
  },
  {
    q: 'How long does the APS process take in 2026?',
    a: 'Standard APS processing takes 3–4 weeks once all documents are submitted. With the interview and document collection, plan for 8–10 weeks total. Apply at least 12 months before your target intake to avoid delays.',
  },
  {
    q: 'How much does it really cost to study in Germany?',
    a: 'Public Universities: €0 tuition (small semester fee €150–350). Private universities: €10,000–25,000/year. Living costs: ~€11,208/year (Blocked Account amount). Total: ₹10–14 L/year — the cheapest among major Western destinations.',
  },
  {
    q: 'What is the Blocked Account and how much money do I need?',
    a: 'The Sperrkonto (Blocked Account) holds €11,208 (raised from €10,332 in Sept 2023 → ~₹10 Lakhs) for your first year. You can withdraw a maximum of €934/month after arriving in Germany. Providers: Fintiba, Expatrio, Deutsche Bank, Kotak Mahindra.',
  },
  {
    q: 'Do I need to learn German to study in Germany?',
    a: 'For English-taught Master\'s programs: NO — you only need IELTS 6.5+ or TOEFL 88+. For Bachelor\'s / German-taught programs: YES — minimum TestDaF 4 or DSH-2 (~B2/C1 level). Learning German A2 still hugely improves your job & PR chances.',
  },
  {
    q: 'What is the Opportunity Card (Chancenkarte)?',
    a: 'Launched June 2024 — a points-based job-seeker visa allowing you to enter Germany WITHOUT a job offer and search for skilled work for up to 12 months. You need a minimum of 6 points across age, degree, language, and work experience.',
  },
  {
    q: 'TU9 vs FH (Fachhochschule) — Which is better?',
    a: 'TU9 (Research Universities) = world-class theory + research + open PhD pathway. FH (Universities of Applied Sciences) = highly practical, includes mandatory paid internships, faster industry entry. Choose TU9 for academia/research; FH for fast career placement.',
  },
  {
    q: 'What are the requirements for a Master\'s in Germany?',
    a: '(1) Bachelor\'s degree with 65–75%+ (TUs want < 2.5 German grade ~ 75%). (2) APS Certificate. (3) IELTS 6.5+ or TestDaF 4 for German programs. (4) SOP, 2 LORs, CV, sometimes GRE for top TUs.',
  },
  {
    q: 'Can I work while studying in Germany?',
    a: 'Yes — full-time students can work 140 full days OR 280 half days per year (raised from 120/240 in Mar 2024). On-campus and Werkstudent (working-student) roles pay €12–18/hr and don\'t count against this limit.',
  },
  {
    q: 'What is the EU Blue Card and how does it help with PR?',
    a: 'The EU Blue Card is a fast-track residence permit for skilled professionals earning €45,300+/year (€41,041 in shortage occupations). With it, you get Germany PR in just 21 months (33 months with A1 German). Without it, standard PR takes 5 years.',
  },
  {
    q: 'What is the post-study job-seeker visa duration?',
    a: 'After completing your German degree, you get an automatic 18-month Job Seeker Visa to find skilled employment in your field. You can work in any role during these 18 months. Once you find a skilled job, you switch to a Work Permit / EU Blue Card.',
  },
  {
    q: 'Which intakes are available in Germany?',
    a: 'Two intakes: Winter Semester (October — main, 75% of programs available) and Summer Semester (April — smaller, mostly research-focused). Application deadlines: 15 July for Winter, 15 January for Summer.',
  },
  {
    q: 'Is health insurance mandatory in Germany?',
    a: 'Yes — health insurance is legally mandatory before you can enroll at any German university. Public insurance (TK, AOK, Barmer): ~€110/month, covers everything. Private (Mawista): cheaper but limited coverage and only valid for students under 30.',
  },
  {
    q: 'Can I bring my spouse to Germany on a student visa?',
    a: 'Yes — your spouse can join you via Family Reunification Visa. They get an Open Work Permit (full work rights) and dependent children get free public schooling. Spouse must show A1 German language proficiency before arrival.',
  },
  {
    q: 'How much can I save / earn after my Master\'s in Germany?',
    a: 'Starting salary for IT/Engineering Master\'s graduates: €48,000–75,000/year (~₹43–67 L). Net take-home after tax: ~60%. With Germany\'s low cost of living, savings of €1,000–1,500/month are realistic in your first job.',
  },
  {
    q: 'What is the difference between Uni-Assist and direct application?',
    a: 'Uni-Assist is a centralised application gateway used by 180+ German universities (€75 for first application, €30 per additional). Some top TUs (TUM, RWTH) require direct portal applications. Always check each university\'s website.',
  },
  {
    q: 'Are there scholarships for Indian students in Germany?',
    a: 'Yes — DAAD (Germany\'s flagship), Deutschlandstipendium (€300/month), Heinrich Böll, Konrad-Adenauer-Stiftung, plus university-specific scholarships at TU Munich, RWTH, Heidelberg. Erasmus+ Joint Master\'s degrees offer full funding.',
  },
  {
    q: 'How safe is Germany for Indian students?',
    a: 'Very safe — Germany ranks Top 20 globally for safety. Public healthcare via mandatory insurance, low violent-crime rates, strong international-student support offices at every university. ~200,000-strong Indian diaspora in Munich, Berlin, Frankfurt.',
  },
  {
    q: 'How is Germany different from UK and Canada for Indian students?',
    a: 'Germany = €0 tuition + 21-month PR via Blue Card + best ROI. UK = 1-year master\'s + global brand but no easy PR. Canada = clear Express Entry PR but high tuition (CAD 18–30k) + 2024 GIC raise to CAD 20,635. Germany wins on cost; Canada wins on PR predictability.',
  },
];

const stats = [
  { val: '40K+', label: 'Indian Students' },
  { val: '€0', label: 'Public Tuition' },
  { val: '21 Mo', label: 'PR via Blue Card' },
  { val: '6+', label: 'Opportunity Card Pts' },
];

const quickLinks = [
  // STRICT URL GOVERNANCE — only the 11 authoritative Germany URLs are exposed
  // here (Feb 16, 2026 sprint directive). Adding any non-authoritative URL
  // creates parallel content paths and violates the cannibalisation rule.
  { label: 'Student Visa Guide',     path: '/study-abroad/germany/student-visa/',           icon: Shield },
  { label: 'APS Certificate',        path: '/study-abroad/germany/aps-certificate/',        icon: FileText },
  { label: 'Blocked Account',        path: '/study-abroad/germany/blocked-account/',        icon: Euro },
  { label: 'Public Universities',    path: '/study-abroad/germany/public-universities/',    icon: Building2 },
  { label: 'Scholarships (DAAD)',    path: '/study-abroad/germany/scholarships/',           icon: Award },
  { label: 'Cost of Study',          path: '/study-abroad/germany/cost-of-study/',          icon: Euro },
  { label: 'Ausbildung Program',     path: '/study-abroad/germany/ausbildung/',             icon: Briefcase },
  { label: 'PR After Study',         path: '/study-abroad/germany/pr-after-study/',         icon: CheckCircle2 },
  { label: 'Germany Intakes',        path: '/study-abroad/germany/intakes/',                icon: Calendar },
  { label: 'Jobs After Masters',     path: '/study-abroad/germany/jobs-after-masters/',     icon: Briefcase },
  { label: 'Student Accommodation',  path: '/study-abroad/germany/student-accommodation/',  icon: Building2 },
];

const IMG_BERLIN =
  'https://images.unsplash.com/photo-1560969184-10fe8719e047?auto=format&fit=crop&w=1920&q=80&fm=webp';
const IMG_MUNICH =
  'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=1920&q=80&fm=webp';

/* ────────────────────────────────────────────────────────────────── */
/* COMPONENT                                                           */
/* ────────────────────────────────────────────────────────────────── */

const GermanyHubPage = () => {
  const [whatsAppHover, setWhatsAppHover] = useState(false);

  usePageMeta(
    'Study in Germany 2026: Ultimate Guide for Indian Students | APS, Blocked Account, Opportunity Card',
    'Complete 2026 guide to study in Germany for Indian students. €0 tuition at public universities, APS certificate process, €11,208 Blocked Account, Opportunity Card points, TU9 universities, 18-month job-seeker visa, EU Blue Card → PR in 21 months. Free counselling with Zafco.',
  );

  useServiceSchema({
    name: 'Germany Study Abroad Consulting for Indian Students',
    description:
      'End-to-end Germany study services for Indian students: APS Certificate guidance, university shortlisting (TU9 + Fachhochschule), Blocked Account, student visa filing, Opportunity Card application, and EU Blue Card → PR pathway support.',
    serviceType: 'Education Consulting',
    areaServed: ['Germany', 'India'],
    audience: 'Indian students pursuing higher education and PR in Germany',
    url: 'https://zafco.in/study-abroad/germany/',
  });

  useFAQSchema(faqs);

  return (
    <div className="bg-white" data-testid="sa-germany-hub-page">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://zafco.in/' },
          { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
          { name: 'Study in Germany', url: 'https://zafco.in/study-abroad/germany/' },
        ]}
      />

      {/* ============================ HERO ============================ */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 55%, #1a1f2e 100%)' }}
        data-testid="germany-hero"
      >
        <div className="absolute inset-0 opacity-[0.18]">
          <img
            src={IMG_BERLIN}
            alt="Brandenburg Gate in Berlin Germany at sunset"
            loading="eager"
            width={1600}
            height={900}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/95 via-[#003087]/85 to-transparent" />
        <div className="absolute top-10 right-[-80px] w-[420px] h-[420px] bg-[#C5A028]/25 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-slate-300/90">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/study-abroad/" className="hover:text-white">Study Abroad</Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-[#C5A028] font-semibold">Germany</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-lg" aria-hidden="true">{getFlagEmoji('DE')}</span>
              <span className="text-[11px] text-white/95 font-bold tracking-[0.18em] uppercase">
                2026 Ultimate Guide
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white leading-[1.05] mb-5">
              Study in <span className="text-[#C5A028]">Germany</span> 2026
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-200 mt-3">— The Ultimate Guide for Indian Students</span>
            </h1>

            <p className="text-base lg:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-2xl">
              €0 public tuition. APS certificate. Blocked Account. Opportunity Card. EU Blue Card →
              PR in 21 months. Get the entire roadmap built by Zafco’s certified Germany counsellors.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link
                to="/study-abroad/book-counselling/?source=germany-hub-hero"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#C5A028]/40"
                data-testid="germany-hero-counselling-cta"
              >
                Book Free Counselling
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/919990533327?text=Hi%20Zafco%2C%20I%27d%20like%20a%20free%20Germany%20study%20counselling%20session."
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setWhatsAppHover(true)}
                onMouseLeave={() => setWhatsAppHover(false)}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5 backdrop-blur-sm"
                data-testid="germany-hero-whatsapp-cta"
              >
                <MessageCircle className={`w-4 h-4 transition-transform ${whatsAppHover ? 'scale-110' : ''}`} />
                WhatsApp: +91-9990533327
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-300/80">
              {['MEA-Aligned Consultancy', '23+ Years Experience', '10,000+ Placements'].map((b) => (
                <div key={b} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#C5A028]" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative bg-black/30 backdrop-blur-md border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold font-syne text-[#C5A028]">{s.val}</div>
                <div className="text-[11px] text-slate-300 mt-0.5 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ APS MANDATORY ALERT ============================ */}
      <section className="bg-gradient-to-r from-amber-50 via-amber-100/40 to-amber-50 border-y-2 border-amber-300" data-testid="germany-aps-alert">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <AlertCircle className="w-6 h-6 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-bold tracking-[0.18em] text-amber-700 uppercase mb-1">
                Critical · Since November 2022
              </div>
              <h2 className="text-lg lg:text-xl font-bold text-slate-900 mb-2">
                APS Certificate is now <span className="text-[#FF1F2D]">MANDATORY</span> for all Indian applicants
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed">
                Without the APS (Akademische Prüfstelle) certificate, your university applications
                and visa WILL be rejected. Plan for 8–10 weeks of processing.{' '}
                <a href="#aps-section" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4">
                  Jump to the full APS process →
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ WHY GERMANY ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="germany-why-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 01 · The Opportunity
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-[#001F5C] leading-tight mb-5">
              Why Germany?
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              Germany is the most affordable Western destination for Indian students — home to{' '}
              <strong className="text-slate-900">40,000+ Indians</strong>, tuition-free public
              universities, and Europe&apos;s strongest engineering & R&amp;D economy.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[#001F5C] text-white">
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase w-1/3">Factor</th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase">Why It Matters for Indian Students</th>
                  </tr>
                </thead>
                <tbody>
                  {keyReasons.map((r, i) => (
                    <tr key={r.factor} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center shrink-0">
                            <r.icon className="w-4 h-4 text-[#C5A028]" />
                          </div>
                          <span className="font-bold text-slate-900 text-sm">{r.factor}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{r.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-slate-200">
              {keyReasons.map((r) => (
                <div key={r.factor} className="p-5 bg-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center">
                      <r.icon className="w-4 h-4 text-[#C5A028]" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm">{r.factor}</h3>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pl-12">{r.details}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image break */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={IMG_MUNICH}
          alt="Munich city skyline with traditional Bavarian architecture"
          loading="lazy"
          width={1600}
          height={600}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/70 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-white/95 text-xl md:text-3xl font-bold font-syne max-w-md leading-snug">
              From Munich to Berlin — Europe’s engineering capital is calling.
            </p>
          </div>
        </div>
      </div>

      {/* ============================ EDUCATION SYSTEM & GRADING ============================ */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="germany-education-system">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 02 · Education System
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Education System &amp; Grading
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Germany has three distinct types of universities — choosing the right one is the
              single biggest decision after picking your course.
            </p>
          </div>

          {/* University Types — Card Grid */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {universityTypes.map((u) => (
              <div key={u.type} className={`bg-gradient-to-br ${u.color} border-2 rounded-2xl p-6`}>
                <h3 className="text-base font-bold font-syne text-slate-900 mb-3">{u.type}</h3>
                <div className="text-xs text-slate-600 mb-3 space-y-2">
                  <div><span className="font-bold text-slate-900">Focus: </span>{u.focus}</div>
                  <div><span className="font-bold text-slate-900">Examples: </span>{u.examples}</div>
                  <div><span className="font-bold text-slate-900">Best for: </span>{u.best}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Grading Scale */}
          <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
            <h3 className="text-lg font-bold font-syne text-slate-900 mb-5 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#003087]" />
              German Grading Scale (1.0 = Best, 5.0 = Fail)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 font-bold text-slate-900">German Grade</th>
                    <th className="text-left py-3 px-4 font-bold text-slate-900">Label</th>
                    <th className="text-left py-3 px-4 font-bold text-slate-900">Indian Equivalent</th>
                    <th className="text-left py-3 px-4 font-bold text-slate-900">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {gradingScale.map((g) => (
                    <tr key={g.grade} className="border-b border-slate-100 hover:bg-amber-50/30">
                      <td className="py-3 px-4 font-bold text-[#001F5C]">{g.grade}</td>
                      <td className="py-3 px-4">{g.label}</td>
                      <td className="py-3 px-4">{g.equiv}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{g.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              <strong>Note: </strong>Most TU9 universities require a German grade <strong>&lt; 2.5 (~75%)</strong> for Master&apos;s admission.
            </p>
          </div>
        </div>
      </section>

      {/* ============================ APS CERTIFICATE (PILLAR) ============================ */}
      <section
        id="aps-section"
        className="py-16 lg:py-24 bg-[#001F5C] text-white relative overflow-hidden"
        data-testid="germany-aps-section"
      >
        <div className="absolute top-20 left-[-100px] w-96 h-96 bg-[#C5A028]/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-[-100px] w-80 h-80 bg-[#FF1F2D]/15 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <div className="flex flex-col items-start gap-3 mb-3">
              <div className="inline-flex items-center gap-2 bg-[#C5A028]/15 border border-[#C5A028]/40 rounded-full px-3 py-1">
                <Star className="w-3.5 h-3.5 text-[#C5A028]" />
                <span className="text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">The Biggest Hurdle</span>
              </div>
              <span className="text-[11px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                Section 03 · APS Certificate
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne leading-tight mb-3">
              APS Certificate — <span className="text-[#C5A028]">Mandatory</span> for Indian Students
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              The APS Certificate (Akademische Prüfstelle) verifies your academic credentials with
              your Indian university. Mandatory since November 2022 — no APS = no admission AND no
              visa.
            </p>
          </div>

          {/* 5-Step Vertical Stepper */}
          <div className="max-w-3xl mx-auto mb-12">
            <ol className="relative border-l-2 border-[#C5A028]/40 ml-4 space-y-7">
              {apsSteps.map((step) => (
                <li key={step.n} className="ml-8 relative">
                  <div className="absolute -left-[3.25rem] w-12 h-12 rounded-full bg-[#C5A028] text-black font-bold text-lg flex items-center justify-center font-syne shadow-lg ring-4 ring-[#001F5C]">
                    {step.n}
                  </div>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#C5A028]/40 transition-colors">
                    <h3 className="text-base font-bold font-syne mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* APS Timeline */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7">
              <h3 className="text-lg font-bold font-syne mb-5 flex items-center gap-2 text-gray-900">
                <Calendar className="w-5 h-5 text-[#C5A028]" />
                APS Timeline 2026 (Working Backwards)
              </h3>
              <div className="space-y-3">
                {apsTimeline.map((t) => (
                  <div key={t.period} className="flex items-start gap-4 pb-3 border-b border-gray-100 last:border-b-0">
                    <div className="text-xs font-bold text-[#8A6F0E] uppercase tracking-wider w-32 shrink-0">{t.period}</div>
                    <div className="text-sm text-gray-700 flex-1">{t.task}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7">
              <h3 className="text-lg font-bold font-syne mb-5 flex items-center gap-2 text-gray-900">
                <HelpCircle className="w-5 h-5 text-[#C5A028]" />
                Common APS Interview Questions
              </h3>
              <ul className="space-y-2.5">
                {apsInterviewTopics.map((q) => (
                  <li key={q} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A028] shrink-0 mt-0.5" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                <strong className="text-amber-800">Pro Tip:</strong> The interview is academic, not personal. Brush up
                on your final-year projects, key subjects from each semester, and have a clear
                career narrative ready.
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/study-abroad/germany/aps-certificate/"
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all"
            >
              Full APS Certificate Guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================ ELIGIBILITY & TOP COURSES ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="germany-eligibility-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 04 · Eligibility &amp; Courses
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Eligibility &amp; Highest-Paying Courses
            </h2>
          </div>

          {/* Eligibility Tables */}
          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-7">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#003087]" />
                Bachelor’s Requirements
              </h3>
              <table className="w-full text-sm">
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">Academic</td><td className="py-2.5">12th with 70%+ + 1 year of Indian Bachelor’s</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">German Language</td><td className="py-2.5">TestDaF 4 / DSH-2 (B2-C1)</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">English Language</td><td className="py-2.5">IELTS 6.5+ (English-taught only)</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">APS Certificate</td><td className="py-2.5 font-bold text-[#FF1F2D]">Mandatory</td></tr>
                  <tr><td className="py-2.5 font-semibold text-slate-900">Studienkolleg</td><td className="py-2.5">Often required (1-year prep)</td></tr>
                </tbody>
              </table>
            </div>

            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-7">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#003087]" />
                Master’s Requirements
              </h3>
              <table className="w-full text-sm">
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">Academic</td><td className="py-2.5">Bachelor’s with 65–75%+ (TUs &lt; 2.5)</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">English-Taught</td><td className="py-2.5">IELTS 6.5–7.0 / TOEFL 88+</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">German-Taught</td><td className="py-2.5">TestDaF 4 / DSH-2</td></tr>
                  <tr className="border-b border-slate-200"><td className="py-2.5 font-semibold text-slate-900">APS Certificate</td><td className="py-2.5 font-bold text-[#FF1F2D]">Mandatory</td></tr>
                  <tr><td className="py-2.5 font-semibold text-slate-900">GRE</td><td className="py-2.5">Recommended for top TUs (300+)</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Courses Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {topCourses.map((c) => (
              <div
                key={c.title}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C5A028] hover:shadow-lg transition-all group"
              >
                <div className="text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-2">{c.category}</div>
                <div className="w-11 h-11 rounded-xl bg-[#003087]/8 flex items-center justify-center mb-4 group-hover:bg-[#003087] transition-colors">
                  <c.icon className="w-5 h-5 text-[#003087] group-hover:text-[#C5A028] transition-colors" />
                </div>
                <h3 className="text-base font-bold font-syne text-slate-900 mb-1">{c.title}</h3>
                <div className="text-[#C5A028] font-bold text-sm mb-1 flex items-center gap-1">
                  <Euro className="w-3.5 h-3.5" />
                  {c.salary}
                </div>
                <div className="text-xs text-slate-500 mb-3">{c.inr}</div>
                <p className="text-xs text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ UNIVERSITY RANKINGS ============================ */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="germany-universities">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 05 · Top Universities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              TU9 &amp; Excellence Universities
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              TU9 is the alliance of Germany’s 9 leading Technical Universities — the global benchmark
              for engineering. Excellence Universities lead in sciences and humanities.
            </p>
          </div>

          {/* TU9 Grid */}
          <div className="mb-12">
            <h3 className="text-lg font-bold font-syne text-[#001F5C] mb-5 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#C5A028]" />
              TU9 Universities (Top Engineering Schools)
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tu9Universities.map((u) => (
                <Link
                  key={u.name}
                  to={`/study-abroad/universities/germany/${u.slug}/`}
                  className="bg-white border-l-4 border-[#003087] rounded-r-xl p-5 hover:shadow-md hover:border-[#C5A028] transition-all group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#003087]">{u.name}</h4>
                    <span className="text-[10px] font-bold bg-[#003087]/10 text-[#003087] px-2 py-0.5 rounded-full">{u.qs}</span>
                  </div>
                  <p className="text-xs text-slate-600">{u.focus}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* Excellence */}
          <div>
            <h3 className="text-lg font-bold font-syne text-[#001F5C] mb-5 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#C5A028]" />
              Excellence Universities (Sciences &amp; Humanities)
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {excellenceUniversities.map((u) => (
                <Link
                  key={u.name}
                  to={`/study-abroad/universities/germany/${u.slug}/`}
                  className="bg-white border-l-4 border-[#C5A028] rounded-r-xl p-5 hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#003087]">{u.name}</h4>
                    <span className="text-[10px] font-bold bg-[#C5A028]/15 text-[#8A6F0E] px-2 py-0.5 rounded-full">{u.qs}</span>
                  </div>
                  <p className="text-xs text-slate-600">{u.focus}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/study-abroad/universities/germany/"
              className="inline-flex items-center gap-2 text-[#003087] font-semibold text-sm hover:gap-3 transition-all"
            >
              View All German Universities <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================ COSTS & BLOCKED ACCOUNT ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="germany-cost">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 06 · Cost &amp; Blocked Account
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Cost of Studying in Germany (2026)
            </h2>
          </div>

          {/* Blocked Account Alert */}
          <div className="mb-8 bg-gradient-to-r from-amber-50 to-amber-100/50 border-l-4 border-amber-500 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-amber-700 uppercase mb-1">
                  Mandatory · Blocked Account
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  €11,208 (~₹10 Lakhs) Sperrkonto Required
                </h3>
                <p className="text-sm text-slate-700">
                  You must deposit €11,208 in a German Blocked Account before your visa is granted.
                  This proves you can support yourself for the first year. Max withdrawal: €934/month.{' '}
                  <Link to="/study-abroad/germany/blocked-account/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4">
                    Complete Blocked Account guide →
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Cost Comparison Table */}
          <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#001F5C] text-white">
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wider uppercase">Country</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wider uppercase">Tuition / Year</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wider uppercase">Living / Year</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wider uppercase">Total (INR)</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {totalCostComparison.map((c, i) => {
                    let rowBg;
                    if (c.country.includes('Germany')) {
                      rowBg = 'bg-[#C5A028]/8 font-semibold';
                    } else if (i % 2 === 0) {
                      rowBg = 'bg-white';
                    } else {
                      rowBg = 'bg-slate-50/60';
                    }
                    return (
                    <tr key={c.country} className={`border-b border-slate-100 ${rowBg}`}>
                      <td className="px-6 py-4">
                        <span className="text-lg mr-2" aria-hidden="true">{c.icon}</span>
                        {c.country}
                        {c.country.includes('Germany') && (
                          <span className="ml-2 text-[10px] font-bold bg-[#C5A028] text-black px-2 py-0.5 rounded-full uppercase">Best ROI</span>
                        )}
                      </td>
                      <td className="px-6 py-4">{c.tuition}</td>
                      <td className="px-6 py-4">{c.living}</td>
                      <td className="px-6 py-4 font-bold text-[#001F5C]">{c.total}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ OPPORTUNITY CARD ============================ */}
      <section
        className="py-16 lg:py-24 relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 60%, #C5A028 200%)' }}
        data-testid="germany-opportunity-card"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A028]/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl mb-10">
            <div className="flex flex-col items-start gap-3 mb-3">
              <div className="inline-flex items-center gap-2 bg-[#C5A028]/15 border border-[#C5A028]/40 rounded-full px-3 py-1">
                <Sparkles className="w-3.5 h-3.5 text-[#C5A028]" />
                <span className="text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">Launched June 2024</span>
              </div>
              <span className="text-[11px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                Section 07 · Opportunity Card
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne leading-tight mb-3">
              Opportunity Card (Chancenkarte)
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              Germany’s new points-based job-seeker visa lets you enter the country WITHOUT a job
              offer and search for skilled work for up to 12 months. Need just{' '}
              <strong className="text-[#FACC15]">6 points</strong> minimum.{' '}
              <Link to="/blogs/germany-opportunity-card-guide" className="text-[#C5A028] underline underline-offset-4 hover:text-[#EAB308]">
                Read the full Opportunity Card playbook →
              </Link>
            </p>
          </div>

          {/* Points Table — light card on dark section for max contrast */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-200">
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-wider uppercase text-gray-800">Criterion</th>
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-wider uppercase text-gray-800">Details</th>
                    <th className="text-right px-6 py-4 text-xs font-bold tracking-wider uppercase text-gray-800">Max Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunityCardPoints.map((p, i) => (
                    <tr key={p.criterion} className={`border-t border-gray-100 hover:bg-amber-50/40 ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                      <td className="px-6 py-3.5 font-bold text-gray-900">{p.criterion}</td>
                      <td className="px-6 py-3.5 text-gray-700 text-xs">{p.detail}</td>
                      <td className="px-6 py-3.5 text-right font-bold text-[#C5A028] text-base">{p.max}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-50 border-t-2 border-amber-200">
                    <td colSpan="2" className="px-6 py-4 font-bold text-gray-900">Minimum to Qualify</td>
                    <td className="px-6 py-4 text-right font-bold text-amber-700 text-lg">6 Points</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Comparison: Opportunity Card vs Student Visa — light cards for readability */}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
              <div className="text-[10px] font-bold tracking-[0.22em] text-amber-600 uppercase mb-2">For Job Seekers</div>
              <h3 className="text-lg font-bold font-syne text-gray-900 mb-3">Opportunity Card</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#C5A028] shrink-0 mt-0.5" /> Stay 12 months · Search for skilled work</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#C5A028] shrink-0 mt-0.5" /> No job offer required upfront</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#C5A028] shrink-0 mt-0.5" /> Allows up to 20 hrs/week trial work</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#C5A028] shrink-0 mt-0.5" /> Need vocational qualification or degree</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#003087] uppercase mb-2">For Students</div>
              <h3 className="text-lg font-bold font-syne text-gray-900 mb-3">Student Visa (D-Type)</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#003087] shrink-0 mt-0.5" /> Duration of program + 18-month Job Seeker</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#003087] shrink-0 mt-0.5" /> Need university admission letter</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#003087] shrink-0 mt-0.5" /> Blocked Account €11,208 required</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#003087] shrink-0 mt-0.5" /> Work 140 full days / 280 half days per year</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ VISA & POST-STUDY WORK ============================ */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="germany-visa-process">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 08 · Visa &amp; PR
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Visa Process &amp; Post-Study Work
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Germany’s National Visa (D-type) is a 6-step process. Processing time: 4–12 weeks.
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-10">
            <ol className="relative border-l-2 border-[#C5A028]/40 ml-4 space-y-7">
              {visaSteps.map((step) => (
                <li key={step.n} className="ml-8 relative">
                  <div className="absolute -left-[3.25rem] w-12 h-12 rounded-full bg-[#003087] text-white font-bold text-lg flex items-center justify-center font-syne shadow-lg ring-4 ring-slate-50">
                    {step.n}
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C5A028] hover:shadow-md transition-all">
                    <h3 className="text-base font-bold font-syne text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* PR Pathway */}
          <div className="bg-gradient-to-r from-[#001F5C] to-[#003087] text-white rounded-2xl p-7 lg:p-9">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C5A028]/20 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-6 h-6 text-[#C5A028]" />
                </div>
                <div>
                  <h3 className="text-lg lg:text-xl font-bold font-syne mb-1">
                    Permanent Residency Pathway
                  </h3>
                  <p className="text-sm text-slate-300 max-w-2xl">
                    Study (2 yrs) → 18-month Job Seeker Visa → Skilled Job → <strong className="text-[#FACC15]">EU Blue Card</strong> → Germany PR in <strong className="text-[#FACC15]">21 months</strong> (33 months with A1 German).
                  </p>
                </div>
              </div>
              <Link
                to="/study-abroad/germany/pr-after-study/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-6 py-3 rounded-full transition-all shrink-0"
              >
                Full PR Guide <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ RESOURCE HUB ============================ */}
      <section className="py-16 bg-white" data-testid="germany-quick-links">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 09 · Resource Hub
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight">
              Deep-Dive into Every Germany Topic
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="bg-white rounded-xl p-5 flex items-start gap-4 group border border-slate-200 hover:border-[#C5A028] hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#003087]/8 flex items-center justify-center shrink-0 group-hover:bg-[#003087] transition-colors">
                  <link.icon className="w-5 h-5 text-[#003087] group-hover:text-[#C5A028] transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#003087] transition-colors">
                    {link.label}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087] mt-1 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ COMPARISONS ============================ */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="germany-comparisons">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 10 · Compare
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Germany vs Other Destinations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                headline: 'Germany vs Canada',
                insight: 'Germany = €0 tuition + 21-month PR via Blue Card. Canada = high tuition + GIC ₹12.5 L but proven Express Entry PR pathway.',
                link: '/study-abroad/comparison/germany-vs-canada/',
                cta: 'Germany vs Canada full breakdown',
              },
              {
                headline: 'Germany vs UK',
                insight: 'Germany = best ROI + EU residency. UK = 1-year master\'s + global brand + Graduate Route Visa.',
                link: '/study-abroad/comparison/germany-vs-uk-for-data-science/',
                cta: 'Germany vs UK for Data Science',
              },
            ].map((c) => (
              <div key={c.headline} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C5A028] hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#C5A028]" />
                  </div>
                  <h3 className="text-lg font-bold font-syne text-slate-900">{c.headline}</h3>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{c.insight}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-1.5 text-[#003087] font-semibold text-xs hover:gap-2 transition-all"
                >
                  {c.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Profile Eval CTA */}
      <SmartProfileEvalCTA country="Germany" country_slug="germany-hub" testId="germany-hub-smart-cta" />

      {/* ============================ FAQ ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="germany-faq">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-10 text-center">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 11 · FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600">
              The 20 most-asked questions by Indian students applying to Germany in 2026.
            </p>
          </div>

          <Accordion type="single" collapsible className="rounded-2xl bg-slate-50 border border-slate-200 px-6 divide-y divide-slate-200">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="border-b-0"
                data-testid={`germany-faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold text-slate-900 hover:no-underline hover:text-[#C5A028] py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-700 leading-relaxed pb-5 pt-0">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============================ FINAL CTA ============================ */}
      <section
        className="relative py-20 overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 60%, #020617 100%)' }}
        data-testid="germany-final-cta"
      >
        <div className="absolute top-0 right-[-80px] w-[420px] h-[420px] bg-[#C5A028]/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-[-80px] w-80 h-80 bg-[#003087] rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
            <Star className="w-3.5 h-3.5" />
            Ready to Apply?
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne mb-4 leading-tight">
            Ready to start your Germany journey?
          </h2>
          <p className="text-base lg:text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Book your free 1:1 counselling. From APS to Blocked Account to Visa — Zafco’s certified
            Germany counsellors guide you every step.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <a
              href="https://wa.me/919990533327?text=Hi%20Zafco%2C%20I%27d%20like%20to%20discuss%20studying%20in%20Germany."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              data-testid="germany-final-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp: +91-9990533327
            </a>
            <a
              href="mailto:studyabroad@zafco.in"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5"
              data-testid="germany-final-email"
            >
              <Mail className="w-4 h-4" />
              studyabroad@zafco.in
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left max-w-3xl mx-auto">
            {[
              { t: 'APS Certificate Filing', icon: FileText },
              { t: 'University Selection', icon: Building2 },
              { t: 'Blocked Account', icon: Euro },
              { t: 'Visa + EU Blue Card', icon: Shield },
            ].map((s) => (
              <div key={s.t} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <s.icon className="w-5 h-5 text-[#C5A028] mb-2" />
                <div className="text-sm font-bold text-white">{s.t}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5 inline mr-1.5" />
            Delhi office hours: Mon–Sat · 10am–7pm IST · MEA-aligned consultancy
          </div>
        </div>
      </section>

      {/* ============================ STICKY MOBILE CTA ============================ */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#001F5C]/95 backdrop-blur-md border-t border-[#C5A028]/30 px-3 py-2.5 flex gap-2"
        data-testid="germany-sticky-mobile-cta"
      >
        <a
          href="https://wa.me/919990533327?text=Hi%20Zafco%2C%20I%27m%20interested%20in%20studying%20in%20Germany."
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-slate-900 text-xs font-bold px-3 py-2.5 rounded-full"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp Us
        </a>
        <Link
          to="/study-abroad/book-counselling/?source=germany-sticky"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#C5A028] text-black text-xs font-bold px-3 py-2.5 rounded-full"
        >
          Free Counselling
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default GermanyHubPage;
